import { connect, ConnectedProps } from 'react-redux'
import ModalCreateEdit from '../../../../../../../baseComponents/ModalsLayouts/ModalCreateEdit'
import { ModalEditComponentProps } from '../../../../../../../baseComponents/ModalsLayouts/types'
import ProductCategories from '../../../../../../../baseComponents/Product/ProductCategories'
import { OnChangeMainCategoryTy } from '../../../../../../../baseComponents/Product/ProductCategories/types'
import { FormElement, FormRender } from '../../../../../../../forms/FormRenderer/types'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import withLoading from '../../../../../../../utils/hoc/withLoading'
import useModalCreateProductLogic from '../../../../../../../utils/hooks/product/useModalCreateProductLogic'
import useLangData from '../../../../../../../utils/hooks/useLangData'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import locations from '../../../../../../../utils/globals/locations'
import { AllReduxPayloads } from '../../../../../../../utils/reducers'
import { getActiveLangs, getConfigParam } from '../../../../../../../utils/reducers/getters'
import { GroupVatPayload } from '../../../../../../../utils/reducers/globals/groupVats'
import { ModifiedCreateProductModelType } from '../../../../../../../specificComponents/Product/types'
import useModalCreateProductSpecificPricesLogic from '../../../../../../../utils/hooks/product/useModalCreateProductSpecificPricesLogic'
import NewProductSpecificPricesTable from '../../../../../../../specificComponents/Product/NewProductSpecificPricesTable'
import TwinTrans from '../../../../../../../baseComponents/TwinTrans'
import { ProductTypesPayload } from '../../../../../../../utils/reducers/products/productTypes'
import { useCallback, useMemo, useState } from 'react'
import useWasteWeightProduct from '../../../../../../../utils/hooks/product/useWasteWeightProduct'
import { InputNumberWithLabelMarginProps, InputWithLabelMarginIntegerStateLess } from '../../../../../../../forms/Input'
import { twinFetchPostJSON } from '../../../../../../../utils/globals/data'
import { CategoryProductModelType } from '@teinor/erp/types/company/category/categoryProduct'
import ReactDOM from 'react-dom'

interface ModalCreateProductProps extends ModalEditComponentProps { 
    customLocation?: string
    navigateToProduct?: boolean
}

const ModalCreateProduct: React.FC<ModalCreateProductProps & ReduxModalCreateProduct> = ({ userPermissions, allRowData, onSubmit, groupVats, productTypes, customLocation, navigateToProduct = true, ...rest }) => {
    const formula = useMemo(() => getConfigParam('referenceGeneration') || '', [])
    const { handleOnSubmit, handleOnError, onChangeMain, reference, setReference, selectedCategory, copyImages, files, setFiles, handleCopyImages } = useModalCreateProductLogic({ customLocation: customLocation || locations.product, onSubmit, navigateToProduct, allRowData: allRowData })
    const { t } = useTwinTranslation()
    const { changeLang, selectedLang, data, changeValCurrentLang } = useLangData({ myData: allRowData?.ProductLangs || null, fieldsToLang: ['name'], keyValue: 'LangId' })
    const { dataPrices, setDataPrices, beforeSubmitAddSpecificPrices } = useModalCreateProductSpecificPricesLogic({ myDataPrices: allRowData?.SpecificPricesProduct })
    const dictProductTypes = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(productTypes || {}))), [productTypes])
    const { dictWasteWeightType, weightInfo, handleChangeWeightInfo } = useWasteWeightProduct({
        basicData: {
            weight: allRowData?.weight || 0,
            calculatedWeight: allRowData?.calculatedWeight || 0,
            wasteWeight: allRowData?.wasteWeight || 0,
            wasteWeightType: allRowData?.wasteWeightType || 0
        } as any
    })
    const extraImageField: FormElement<keyof ModifiedCreateProductModelType>[] = []
    if (allRowData?.id) {
        extraImageField.push({
            name: 'copyImages',
            component: 'CheckboxMargin',
            label: t('copyProductImages', 'Copiar imágenes del producto'),
            value: String(copyImages),
            defaultChecked: copyImages,
            onChange: (value) => handleCopyImages(value)
        })
    }
    const fields: FormRender<ModifiedCreateProductModelType> = [
        {
            cols: 2,
            elements: [
                {
                    name: 'LangId',
                    label: t('language', 'Idioma'),
                    component: 'CustomSelectWithSearchBar',
                    items: getActiveLangs(),
                    value: selectedLang,
                    onChange: changeLang,
                    required: true
                },
                {
                    name: 'name',
                    label: t('name', 'Nombre'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                    lang: selectedLang,
                    value: data?.['name'][selectedLang] ?? '',
                    onChange: (name) => changeValCurrentLang('name', name)
                },
                {
                    name: 'nameLangs',
                    component: 'InputHidden',
                    value: JSON.stringify(data?.name)
                },
                {
                    name: 'id',
                    component: 'InputHidden',
                    value: allRowData?.id || undefined
                },
                {
                    name: 'reference',
                    label: t('reference', 'referencia'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                    required: true,
                    value: reference,
                    onChange: (value) => setReference(value)
                },
                {
                    name: 'GroupVatId',
                    label: t('groupVat', 'Grupo de impuestos'),
                    component: 'CustomSelectWithSearchBar',
                    items: groupVats || {},
                    value: allRowData?.GroupVatId || '1',
                    required: true
                },
                {
                    name: 'productType',
                    label: t('classProduct', 'Clase de producto'),
                    component: 'CustomSelectWithSearchBar',
                    items: addDictionaryExtraLine({ 3: { name: t('productWithSerialNumber', 'Producto con número de serie') }, 4: { name: t('lotProduct', 'Producto con lotes') } }),
                    sortItems: true,
                    value: allRowData?.productType
                },
                {
                    label: t('productTypeSelector', 'Tipo de producto'),
                    name: 'ProductTypeId',
                    component: 'CustomSelectWithSearchBar',
                    items: dictProductTypes,
                    sortItems: true,
                    value: allRowData?.ProductTypeId
                },
                {
                    label: t('weight', 'Peso'),
                    name: 'weight',
                    component: 'InputNumberWithLabelMargin',
                    value: weightInfo?.weight || '',
                    onChange: (val)=>handleChangeWeightInfo('weight', val ),
                },
                {
                    label: t('wasteWeightType', 'Tipo de merma'),
                    name: 'wasteWeightType',
                    component: 'CustomSelectWithSearchBar',
                    items: dictWasteWeightType,
                    value: weightInfo?.wasteWeightType || '0',
                    onChange: (val) => handleChangeWeightInfo('wasteWeightType', val),
                },
                {
                    label: t('calculatedWeight', 'Peso calculado'),
                    name: 'calculatedWeight',
                    component: 'InputHidden',
                    disabled: true,
                    value: weightInfo?.weight || '',
                },
                {
                    name: 'code_bar',
                    label: t('codebar', 'Código de Barras'),
                    component: 'Custom',
                    render: (props) => <CustomInputNumberBarcodeProduct {...props} />,
                    value: ''
                },
                ...extraImageField
            ]
        },
        {
            cols: 2,
            elements: [
                {
                    name: 'categories',
                    component: 'Custom',
                    render: ModalProductCategories,
                    extraProps: { onChangeMain, productType: allRowData?.productType, parsedCategories: allRowData?.parsedCategories, categoryProducts: allRowData?.CategoriesProducts }
                },
                {
                    name: 'image',
                    label: t('imageProduct', 'Imagen del producto'),
                    component: 'ImageInputStateLess',
                    defaultValue: copyImages ? allRowData?.ProductImages?.[0]?.url : null,
                    readOnly: copyImages,
                    files: files,
                    setFiles: setFiles
                },
            ]
        }
    ]
    if (formula?.includes('{cat_ref}') || formula?.includes('{counter}')) {
        fields[0].elements.push({
            name: 'catForCounter',
            component: 'InputHidden',
            value: selectedCategory || undefined
        })
    }

    return (<ModalCreateEdit fields={fields} url={'/api/app/product/createProduct'} beforeSubmit={beforeSubmitAddSpecificPrices} translations={{
        title: t('createProduct', 'Crear Producto'),
        button: t('create', 'Crear')
    }} haveButtonPermissions={true} onSubmit={handleOnSubmit} onChangeError={handleOnError} {...rest} className='modal_create_product notFlexAutoFormRender' imagePath={['tmp']}>
        <div className='regular18 my-20'>
            <TwinTrans transKey='prices'>Precios</TwinTrans>
        </div>
        <NewProductSpecificPricesTable dataPrices={dataPrices} setDataPrices={setDataPrices} />
    </ModalCreateEdit>)
}

interface CustomInputNumberBarcodeProductProps extends InputNumberWithLabelMarginProps {}

const CustomInputNumberBarcodeProduct: React.FC<CustomInputNumberBarcodeProductProps> = ({ name, label, value }) => {
    const { myCodebar, setMyCodebar, assignAutomaticBarcode } = useCustomInputNumberBarcodeProductLogic({value})
    return (
        <div className='relative'>
            <div className='text-green-21 regular12 z-3 cursor-pointer absolute right-0' onClick={assignAutomaticBarcode}><TwinTrans transKey='assignBarcode'>Asignar código de barras</TwinTrans></div>
            <InputWithLabelMarginIntegerStateLess name={name} label={label} value={myCodebar} onChange={setMyCodebar} />
        </div>
    )
}

interface CustomInputNumberBarcodeProductLogicProps {
    value: string | number
}

const useCustomInputNumberBarcodeProductLogic = ({ value }: CustomInputNumberBarcodeProductLogicProps) => {
    const [myCodebar, setMyCodebar] = useState(value)

    const assignAutomaticBarcode = useCallback(async () => {
        const result: TwinDictionary = await twinFetchPostJSON('/api/app/product/getNextProductBarcodeCounter', {})
        if (result) {
            setMyCodebar(result.value)
        }
    }, [setMyCodebar])

    return { myCodebar, setMyCodebar, assignAutomaticBarcode }
}

interface ModalProductCategoriesProps {
    extraProps: {
        onChangeMain: OnChangeMainCategoryTy
        productType?: number
        parsedCategories?: number[]
        categoryProducts?: CategoryProductModelType[] 
    }
}

const ModalProductCategories: React.FC<ModalProductCategoriesProps> = ({ extraProps }) => {
    return (<ProductCategories productType={extraProps.productType || 0 as any} selectedCategories={extraProps.parsedCategories || [1]} className='createproduct_categories' onChangeMain={extraProps.onChangeMain} categoryProducts={extraProps.categoryProducts || undefined} />)
}

interface ModalCreateProductOutsideProps extends ModalCreateProductProps { }

export const ModalCreateProductOutside: React.FC<ModalCreateProductOutsideProps> = ({ ...rest }) => {
    return (
        ReactDOM.createPortal(
            <ModalCreateProductConnect {...rest as any} />,
            document.body
        )
    )
}

const modalCreateProductDispatch = {
    setGroupVats: (payload: GroupVatPayload) => ({ type: 'CHANGE_GROUPVAT', payload }),
    setProductTypes: (payload: ProductTypesPayload) => ({ type: 'CHANGE_PRODUCT_TYPES', payload }),
}

export type ReduxModalCreateProduct = ConnectedProps<typeof modalCreateProductConnect>
const mapModalCreateProductConnector = (state: AllReduxPayloads) => ({ groupVats: state.groupVats, productTypes: state.productTypes })
const modalCreateProductConnect = connect(mapModalCreateProductConnector, modalCreateProductDispatch)

const modalCreateProductConnectLoading = withLoading(ModalCreateProduct, [{ fetchUrl: '/api/app/config/vat/getAllGroupsVat', propName: 'groupVats', setFunctionName: 'setGroupVats' }, { fetchUrl: '/api/app/product/productType/getAllProductTypes', propName: 'productTypes', setFunctionName: 'setProductTypes' }])

const ModalCreateProductConnect = modalCreateProductConnect(modalCreateProductConnectLoading)

export default ModalCreateProductConnect